import { Component, Inject, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ColumnHeader } from '../../ressources/constants/colHeaders';

interface Roles {
  id: number;
  name: string;
  description: string;
}
interface Categories {
  id: number;
  label: string;
  path: string;
  import_sons: boolean;
  preselect_playlist: boolean;
}
interface Stations {
  id: number;
  label: string;
}

@Component({
  selector: 'app-user-edit',
  templateUrl: './userEdit.component.html',
  styleUrls: ['./userEdit.component.scss']
})
export class UserEditComponent implements OnInit, AfterViewInit {

  @ViewChild('categorieTab') categorieTab;
  @ViewChild('catLabel') catLabel;

  roleSelected;
  categorieSelected;
  stationsSelected;
  hiddencolumnsSelected: number[] = []; // Array d'indices de colonnes

  preferNickname = false;
  lowBitrateAllowed = false;

  stationsFromDefault = false;      // true si stations pré-chargées depuis DU_config
  hiddencolumnsFromDefault = false; // true si hiddencolumns pré-chargées depuis DU_config

  private defaultStations: number[] = [];      // valeurs initiales du rôle (pour comparaison au save)
  private defaultHiddencolumns: number[] = []; // valeurs initiales du rôle (pour comparaison au save)

  editedUser;
  roles: Roles[] = [];
  categories: Categories[] = [];
  stations: Stations[] = [];
  availableColumns: ColumnHeader[] = [];
  userExisting = false;

  constructor(public dialogRef: MatDialogRef<any>,
              @Inject(MAT_DIALOG_DATA) private dataIn: any) {
    console.log(dataIn);
    this.editedUser = dataIn.element;
    this.roles = dataIn.roles;
    this.categories = dataIn.categories;
    this.stations = dataIn.stations;
    this.availableColumns = dataIn.availableColumns || [];
    this.userExisting = dataIn.userExisting;

    // Trouver la config par défaut du rôle effectif dans DU_config
    const roleConfigs: any[] = dataIn.roleConfigs || [];
    const effectiveRoleId = this.editedUser.effective_role_id;
    const roleConfig = roleConfigs.find(c => c.role === effectiveRoleId) || null;

    // Initialiser prefer_nickname depuis les données utilisateur
    this.preferNickname = !!this.editedUser.prefer_nickname;

    // Initialiser low_bitrate_allowed depuis les données utilisateur (réglage admin uniquement)
    this.lowBitrateAllowed = !!this.editedUser.low_bitrate_allowed;

    // Initialiser avec les valeurs personnalisées si elles existent
    if (this.editedUser.custom_role_id) {
      this.roleSelected = this.editedUser.custom_role_id;
    }
    if (this.editedUser.categorie) {
      this.categorieSelected = this.editedUser.categorie;
    }
    if (this.editedUser.stations) {
      this.stationsSelected = this.editedUser.stations.split(',').map(x => Number(x));
    } else if (roleConfig && roleConfig.stations) {
      // Pas de config custom → pré-charger les stations par défaut du rôle
      this.stationsSelected = roleConfig.stations.split(',').map((x: string) => Number(x)).filter((x: number) => !isNaN(x));
      this.stationsFromDefault = true;
      this.defaultStations = [...this.stationsSelected];
    }
    // Parser le JSON des colonnes cachées
    if (this.editedUser.hiddencolumns) {
      try {
        this.hiddencolumnsSelected = JSON.parse(this.editedUser.hiddencolumns);
      } catch (e) {
        console.error('Erreur lors du parsing des colonnes cachées:', e);
        this.hiddencolumnsSelected = [];
      }
    } else if (roleConfig && roleConfig.hiddencolumns) {
      // Pas de config custom → pré-charger les colonnes cachées par défaut du rôle
      try {
        this.hiddencolumnsSelected = JSON.parse(roleConfig.hiddencolumns);
        this.hiddencolumnsFromDefault = true;
        this.defaultHiddencolumns = [...this.hiddencolumnsSelected];
      } catch (e) {
        console.error('Erreur lors du parsing des colonnes cachées du rôle:', e);
      }
    }
  }

  ngOnInit() {}

  ngAfterViewInit() {}

  private arraysEqual(a: number[], b: number[]): boolean {
    if (a.length !== b.length) return false;
    const sa = [...a].sort((x, y) => x - y);
    const sb = [...b].sort((x, y) => x - y);
    return sa.every((v, i) => v === sb[i]);
  }

  returnDescRole() {
    if (this.roleSelected && this.roles.find(c => c.id === this.roleSelected).description) {
      return this.roles.find(c => c.id === this.roleSelected).description;
    } else {
      return '';
    }
  }

  returnData() {
    const data: any = {};

    // Rôle : null si non sélectionné (utilise le rôle par défaut)
    if (this.roleSelected) {
      data.role = this.roleSelected;
    } else {
      data.role = null;
    }

    // Catégorie : null si non sélectionnée (toutes les catégories)
    if (this.categorieSelected) {
      data.categorie = this.categorieSelected.toString();
    } else {
      data.categorie = null;
    }

    // Stations : null si vide ou si valeur identique au défaut du rôle (pas de surcharge inutile)
    if (this.stationsSelected && this.stationsSelected.length > 0) {
      const isDefaultStations = this.stationsFromDefault && this.arraysEqual(this.stationsSelected, this.defaultStations);
      data.stations = isDefaultStations ? null : this.stationsSelected.join(',');
    } else {
      data.stations = null;
    }

    // Colonnes cachées : null si vide ou si valeur identique au défaut du rôle (pas de surcharge inutile)
    if (this.hiddencolumnsSelected && this.hiddencolumnsSelected.length > 0) {
      const isDefaultHidden = this.hiddencolumnsFromDefault && this.arraysEqual(this.hiddencolumnsSelected, this.defaultHiddencolumns);
      data.hiddencolumns = isDefaultHidden ? null : JSON.stringify(this.hiddencolumnsSelected);
    } else {
      data.hiddencolumns = null;
    }

    // Préférence de surnom
    data.preferNickname = this.preferNickname;

    // Autorisation de bitrate réduit (réglage admin uniquement)
    data.lowBitrateAllowed = this.lowBitrateAllowed;

    // Nouvelle catégorie
    if (this.categorieTab && this.categorieTab._selectedIndex === 1) {
      data.newCategorie = true;
      if (this.catLabel.nativeElement.value.length > 0) {
        data.newCatLabel = this.catLabel.nativeElement.value;
      }
    } else {
      data.newCategorie = false;
    }

    return data;
  }

  cancel() {
    this.dialogRef.close();
  }

  /**
   * Vérifie si une nouvelle catégorie existe déjà (label)
   */
  checkNewCategorieExists(): boolean {
    if (!this.catLabel) {
      return false;
    }

    const newLabel = this.catLabel.nativeElement.value.trim();
    if (!newLabel) {
      return false;
    }

    return this.categories.some(cat => cat.label.toLowerCase() === newLabel.toLowerCase());
  }

  /**
   * Vérifie si le bouton Enregistrer doit être désactivé
   */
  isSaveDisabled(): boolean {
    // Si on est sur l'onglet "Ajouter" une nouvelle catégorie
    if (this.categorieTab && this.categorieTab._selectedIndex === 1) {
      return this.checkNewCategorieExists();
    }
    return false;
  }

  /**
   * Enregistre les préférences de l'utilisateur (création ou modification)
   */
  saveSettings() {
    const formatData = this.returnData();

    // Si on crée une nouvelle catégorie
    if (formatData.newCategorie) {
      // Vérifier que le label est rempli
      if (!formatData.newCatLabel) {
        return; // Ne rien faire si le champ est vide
      }

      // Vérifier les doublons
      if (this.checkNewCategorieExists()) {
        return; // Ne rien faire si doublon détecté (l'erreur est affichée dans le template)
      }
    }

    this.dialogRef.close({
      action: 'save',
      data: formatData
    });
  }

  /**
   * Supprime les préférences personnalisées de l'utilisateur
   */
  removeSettings() {
    const formatData = this.returnData();
    this.dialogRef.close({
      action: 'remove',
      data: formatData
    });
  }

}
